import * as THREE from 'three';
import { settings, type Settings } from '../../app/settings';

export function webglAvailable(): boolean {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch {
    return false;
  }
}

type FrameFn = (dt: number, time: number) => void;

/**
 * Renderizador, escena y bucle principal. El renderizado va a la frecuencia del monitor;
 * la simulación de red/física usa su propio paso fijo (ver GameSession).
 */
export class Engine {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(60, 1, 0.1, 400);
  readonly sun = new THREE.DirectionalLight(0xfff4e0, 2.2);
  readonly hemi = new THREE.HemisphereLight(0xbfe3ff, 0x5a7d3a, 1.1);
  private frameFns = new Set<FrameFn>();
  private last = performance.now();
  private running = false;
  private reported = new Set<string>();
  fps = 60;
  private fpsAcc = 0;
  private fpsFrames = 0;
  /** Objeto que la luz del sol sigue (para sombras nítidas alrededor del jugador). */
  shadowTarget: THREE.Vector3 | null = null;
  readonly info: { maxTexture: number; renderer: string };

  constructor(container: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({ antialias: settings.get().quality !== 'low', powerPreference: 'high-performance' });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.domElement.className = 'game-canvas';
    container.append(this.renderer.domElement);
    const gl = this.renderer.getContext();
    this.info = { maxTexture: gl.getParameter(gl.MAX_TEXTURE_SIZE), renderer: String(gl.getParameter(gl.VERSION)) };

    this.scene.background = new THREE.Color(0x8fd3ff);
    this.scene.fog = new THREE.Fog(0xbfe6ff, 60, 160);
    this.scene.add(this.hemi);
    this.sun.position.set(40, 70, 25);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    const sc = this.sun.shadow.camera;
    sc.left = sc.bottom = -45;
    sc.right = sc.top = 45;
    sc.near = 1;
    sc.far = 200;
    this.sun.shadow.bias = -0.0005;
    this.scene.add(this.sun, this.sun.target);

    this.applySettings(settings.get());
    settings.subscribe((s, prev) => {
      if (s.shadows !== prev.shadows || s.renderScale !== prev.renderScale || s.viewDistance !== prev.viewDistance || s.quality !== prev.quality)
        this.applySettings(s);
    });
    window.addEventListener('resize', () => this.resize());
    this.resize();
  }

  applySettings(s: Settings) {
    this.renderer.shadowMap.enabled = s.shadows;
    this.sun.castShadow = s.shadows;
    this.sun.shadow.mapSize.set(s.quality === 'high' ? 2048 : 1024, s.quality === 'high' ? 2048 : 1024);
    this.sun.shadow.map?.dispose();
    this.sun.shadow.map = null;
    const fog = this.scene.fog as THREE.Fog;
    fog.near = s.viewDistance * 0.4;
    fog.far = s.viewDistance;
    this.camera.far = s.viewDistance + 20;
    this.camera.updateProjectionMatrix();
    this.resize();
    this.scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh && m.material) (Array.isArray(m.material) ? m.material : [m.material]).forEach((mat) => (mat.needsUpdate = true));
    });
  }

  resize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const scale = settings.get().renderScale;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2) * scale);
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  onFrame(fn: FrameFn): () => void {
    this.frameFns.add(fn);
    return () => this.frameFns.delete(fn);
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    const loop = (now: number) => {
      if (!this.running) return;
      const dt = Math.min(0.1, (now - this.last) / 1000);
      this.last = now;
      this.fpsAcc += dt;
      this.fpsFrames++;
      if (this.fpsAcc > 0.5) {
        this.fps = Math.round(this.fpsFrames / this.fpsAcc);
        this.fpsAcc = 0;
        this.fpsFrames = 0;
      }
      for (const fn of this.frameFns) {
        // Un error en un subsistema no debe congelar el bucle de render
        try {
          fn(dt, now / 1000);
        } catch (err) {
          if (!this.reported.has(String(err))) {
            this.reported.add(String(err));
            console.error('Error en el bucle de juego:', err);
          }
        }
      }
      if (this.shadowTarget) {
        this.sun.position.set(this.shadowTarget.x + 30, this.shadowTarget.y + 60, this.shadowTarget.z + 20);
        this.sun.target.position.copy(this.shadowTarget);
      }
      this.renderer.render(this.scene, this.camera);
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }

  stop() {
    this.running = false;
  }
}
